import React, { useEffect, useState } from "react";
import Layout from "../../common/layout";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { addPageObject } from "../../../../store/actions/pages";
import { OrgInput } from "./styels";
import FormInput from "../../../elements/input";
import { Tree, TreeNode } from "react-organizational-chart";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { MdExpandMore, MdExpandLess } from "react-icons/md";
import styled from "styled-components";
import { ProfileIcon } from "../../common/layout/styels";
import { generateThumbnail } from "../../../functions/string";

// Styled Components
const NodeContainer = styled.div`
  display: flex;
  justify-content: center;
`;
const NodeItem = styled.div`
  padding: 10px;
  border: 1px solid #d6e0ff;
  border-radius: 10px;
  background-color: ${(props) => (props.isSelected ? "#d6e0ff" : "#FFF")};
  // text-align: center;
  position: relative;
  width: fit-content;
  display: flex;
  // align-items: center;
  gap: 10px;
`;

const RoleText = styled.div`
  font-size: 12px;
  color: gray;
  text-align: left;
  margin-top: 5px;
`;

const DepartmentTag = styled.div`
  background-color: ${(props) => props.color};
  color: white;
  padding: 2px 5px;
  border-radius: 5px;
  font-size: 10px;
  margin-top: 5px;
`;

const ExpandButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  position: absolute;
  right: 0px;
  bottom: -4px;
  // transform: translateY(-50%);
`;

const ExpandAllButton = styled.button`
  background: #007bff;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;
  margin-bottom: 10px;

  &:hover {
    background: #0056b3;
  }
`;

// Function to get department colors
const getDepartmentColor = (department) => {
  switch (department.toLowerCase()) {
    case "vertrieb": return "green";
    case "marketing": return "blue";
    case "operations": return "orange";
    case "buchhaltung": return "teal";
    default: return "gray";
  }
};

const Organization = (props) => {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const [initialized, setInitialized] = useState(false);
  const [treeData, setTreeData] = useState(null);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [isTreeExpanded, setIsTreeExpanded] = useState(false); // Track if the entire tree is expanded

  const dashboard = useSelector((state) =>
    state.pages[`user/hierarchy`]
      ? state.pages[`user/hierarchy`]
      : {
          data: null,
          isLoading: true,
          error: null,
        }
  );

  useEffect(() => {
    if (initialized) {
      dispatch(addPageObject("user/hierarchy", 0, {}));
    }
  }, [initialized, dispatch]);

  useEffect(() => {
    document.title = `${t("organization")} - PersoBase`;
  }, [t]);

  useEffect(() => {
    props.setLoaderBox(dashboard.isLoading);
    dashboard.isLoading && setInitialized(true);
  }, [dashboard, props]);

  // Transform data for react-organizational-chart
  const transformHierarchy = (companyName, admins, locations) => {
    const hierarchy = {
      _id: "company",
      name: companyName,
      attributes: { role: "Company" },
      children: [
        ...admins?.map((admin) => ({
          _id: admin._id,
          name: admin?.name || "Admin",
          attributes: { role: "Admin" },
          children: [],
        })),
        ...locations?.map((location) => ({
          _id: location._id,
          name: location?.location?.title || "Other Location",
          attributes: { role: "Location" },
          children: location?.departments?.map((department) => ({
            _id: department._id,
            name: department?.department?.title || "Other Department",
            attributes: { role: "Department" },
            children: department?.places?.map((place) => ({
              _id: place._id,
              name: place?.place?.title || "Other Place",
              attributes: { role: "Project" },
              children: (place?.employees || []).map((employee) => ({
                _id: employee._id,
                name: `${employee.firstName} ${employee.lastName}`,
                attributes: { role: "Employee", designation: employee?.designation?.title },
              })),
            })),
          })),
        })),
      ],
    };

    return hierarchy;
  };

  useEffect(() => {
    if (dashboard.data) {
      const data = transformHierarchy(dashboard.data.company, dashboard.data.admins, dashboard.data.locations);
      setTreeData(data);
    }
  }, [dashboard.data]);

  // Function to find and expand the path to the selected node
  const expandPathToNode = (node, targetId, path = []) => {
    if (node._id === targetId) {
      setExpandedNodes((prev) => new Set([...prev, ...path.map((n) => n._id), targetId]));
      return true;
    }

    if (node.children) {
      for (const child of node.children) {
        if (expandPathToNode(child, targetId, [...path, node])) {
          return true;
        }
      }
    }

    return false;
  };

  // Function to expand or collapse the entire tree
  const toggleTreeExpansion = () => {
    if (isTreeExpanded) {
      setExpandedNodes(new Set()); // Collapse all nodes
    } else {
      const allNodeIds = [];
      const collectNodeIds = (node) => {
        allNodeIds.push(node._id);
        if (node.children) {
          node.children.forEach(collectNodeIds);
        }
      };
      collectNodeIds(treeData);
      setExpandedNodes(new Set(allNodeIds)); // Expand all nodes
    }
    setIsTreeExpanded(!isTreeExpanded);
  };

  const handleSearch = (id) => {
    setSelectedNodeId(id);
    if (treeData) {
      expandPathToNode(treeData, id);
    }
    setTimeout(() => {
      const nodeElement = document.getElementById(id);
      if (nodeElement) {
        nodeElement.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
      }
    }, 200);
  };

  const renderTree = (node) => {
    const isExpanded = expandedNodes.has(node._id);
    const isSelected = selectedNodeId === node._id;

    return (
      <TreeNode
        key={node._id}
        label={
          <NodeContainer>
            <NodeItem id={node._id} isSelected={isSelected}>
              <ProfileIcon>{generateThumbnail(node.name ?? "", null, node.photo ?? "")}</ProfileIcon>
              <div>
                <div style={{fontSize:'14px', fontWeight:'bold'}}>{node.name}</div>
                <RoleText>{node.attributes?.role}</RoleText>
                {node.attributes?.department && (
                  <DepartmentTag color={getDepartmentColor(node.attributes.department)}>
                    {node.attributes.department.toUpperCase()}
                  </DepartmentTag>
                )}
                {node.children?.length > 0 && (
                  <ExpandButton
                    onClick={() => {
                      if (isExpanded) {
                        setExpandedNodes((prev) => {
                          const newSet = new Set(prev);
                          newSet.delete(node._id);
                          return newSet;
                        });
                      } else {
                        setExpandedNodes((prev) => new Set([...prev, node._id]));
                      }
                    }}
                  >
                    {isExpanded ? <MdExpandLess size={20} /> : <MdExpandMore size={20} />}
                  </ExpandButton>
                )}
              </div>
            </NodeItem>
          </NodeContainer>
        }
      >
        {isExpanded && node.children?.map((child) => renderTree(child))}
      </TreeNode>
    );
  };

  const [attribute] = useState({
    type: "select",
    placeholder: "searchEmployee",
    name: "user",
    validation: "",
    showItem: "fullName",
    default: "",
    label: "employee",
    required: false,
    view: false,
    tag: true,
    add: true,
    update: true,
    apiType: "API",
    selectApi: "user/selectAll",
  });

  const filterChange = (sl, option, name) => {
    setSelectedNodeId(option?.id);
    handleSearch(option?.id);
  };

  return (
    <div style={{ width: "100%", height: "100vh", padding: "20px" }}>
      <OrgInput>
        <FormInput
          {...attribute}
          value={selectedNodeId}
          key={`input`}
          type={"select"}
          onChange={filterChange}
          search={true}
        />
      </OrgInput>
      <ExpandAllButton onClick={toggleTreeExpansion}>
        {isTreeExpanded ? "Collapse All" : "Expand All"}
      </ExpandAllButton>
      {treeData && (
        <TransformWrapper onZoom={0.2}>
          <TransformComponent wrapperStyle={{width:'100%',height:'100%', marginTop:'-100px'}}>
            <Tree
              lineWidth={"1px"}
              lineColor={"#3FC4FD"}
              lineBorderRadius={"10px"}
              label={renderTree(treeData)}

            />
          </TransformComponent>
        </TransformWrapper>
      )}
    </div>
  );
};

export default Layout(Organization);